import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, DollarSign, Brain, Plus, RefreshCw, Link2 } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { useBudget } from '../hooks/useBudget';
import { budgetService } from '../../backend/services/budget';
import { BudgetSummaryCard } from '../components/budget/BudgetSummaryCard';
import { BudgetCategoryList } from '../components/budget/BudgetCategoryList';
import { BudgetSuggestionPanel } from '../components/budget/BudgetSuggestionPanel';
import { BudgetMetaMappingPanel } from '../components/budget/BudgetMetaMappingPanel';
import { metaAdsService } from '../../backend/services/metaAds';
import type { BudgetSuggestion, ReallocationSuggestion } from '../../shared/types/budget';

export const CampaignBudget: React.FC = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const { summary, budgets, loading, error, createBudget } = useBudget(campaignId);
  
  const [suggestions, setSuggestions] = React.useState<BudgetSuggestion[]>([]);
  const [reallocations, setReallocations] = React.useState<ReallocationSuggestion[]>([]);
  const [aiLoading, setAiLoading] = React.useState(false);
  const [showSuggestions, setShowSuggestions] = React.useState(false);
  const [syncLoading, setSyncLoading] = React.useState(false);
  const [mappings, setMappings] = React.useState<Record<string, { id: string; external_campaign_id: string } | null>>({});

  // Load existing mappings for the budgets on first load
  React.useEffect(() => {
    const load = async () => {
      if (!budgets.length) return;
      const res = await metaAdsService.getBudgetMetaMappings(budgets.map(b => b.id));
      const byBudget: Record<string, { id: string; external_campaign_id: string } | null> = {};
      budgets.forEach(b => { byBudget[b.id] = null; });
      res.forEach((m: any) => { if (m.budget_id) byBudget[m.budget_id] = { id: m.id, external_campaign_id: m.external_campaign_id }; });
      setMappings(byBudget);
    };
    load();
  }, [budgets.map(b => b.id).join(',')]);

  const handleSyncMeta = async () => {
    if (!campaignId) return;
    // Choose a budget that has a mapping; if multiple, prompt; else require mapping
    const mapped = Object.entries(mappings).filter(([, m]) => !!m);
    let budgetId: string | undefined;
    let externalCampaignId: string | undefined;
    if (mapped.length === 1) {
      budgetId = mapped[0][0];
      externalCampaignId = mapped[0][1]?.external_campaign_id;
    } else if (mapped.length > 1) {
      const choice = window.prompt(`Enter Budget ID to sync from these mapped budgets:\n${mapped.map(([id, m]) => `${id} → ${m?.external_campaign_id}`).join('\n')}`);
      if (!choice) return; 
      budgetId = choice;
      externalCampaignId = mappings[choice!]?.external_campaign_id;
    } else {
      alert('No Meta mapping found. Add a Meta Campaign ID to a budget below first.');
      return;
    }

    const until = new Date();
    const since = new Date(until.getTime() - 3 * 3600 * 1000); // last 3 hours
    setSyncLoading(true);
    try {
      await budgetService.syncMetaSpendForBudget(
  budgetId!,
  externalCampaignId!,
        since.toISOString(),
        until.toISOString()
      );
      // Refresh budgets/summary
      (await import('../hooks/useBudget'));
      // Easiest: force a reload of page data by navigating to same route
      navigate(0);
    } catch (e) {
      console.error('Meta sync failed', e);
      alert('Meta sync failed; check console');
    } finally {
      setSyncLoading(false);
    }
  };

  const loadAISuggestions = async () => {
    if (!campaignId) return;
    
    setAiLoading(true);
    try {
      const [suggestionsData, reallocationsData] = await Promise.all([
        budgetService.generateBudgetSuggestions(campaignId),
        budgetService.analyzeReallocationOpportunities(campaignId)
      ]);
      
      setSuggestions(suggestionsData);
      setReallocations(reallocationsData);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Failed to load AI suggestions:', error);
    } finally {
      setAiLoading(false);
    }
  };

  const handleAcceptSuggestion = async (suggestion: BudgetSuggestion) => {
    if (!campaignId) return;
    
    try {
      await createBudget({
        campaign_id: campaignId,
        category_id: suggestion.category_id,
        name: `AI Suggested Budget`,
        allocated_amount: suggestion.suggested_amount,
        spent_amount: 0,
        notes: `AI Suggestion: ${suggestion.reasoning}`,
        row_order: budgets.length
      });
    } catch (error) {
      console.error('Failed to accept suggestion:', error);
    }
  };

  const handleRejectSuggestion = (suggestion: BudgetSuggestion) => {
    // Remove from suggestions list
    setSuggestions(prev => prev.filter(s => s.category_id !== suggestion.category_id));
  };

  const handleAcceptReallocation = (reallocation: ReallocationSuggestion) => {
    // TODO: Implement budget reallocation logic
    console.log('Accepting reallocation:', reallocation);
    setReallocations(prev => prev.filter(r => r !== reallocation));
  };

  const handleRejectReallocation = (reallocation: ReallocationSuggestion) => {
    setReallocations(prev => prev.filter(r => r !== reallocation));
  };

  return (
    <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-full">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-4">
          <button
            onClick={() => navigate(`/campaign/${campaignId}`)}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mb-3"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Campaign
          </button>
          <PageHeader
            icon={<DollarSign className="h-8 w-8" />}
            title="Campaign Budget"
            subtitle={`Manage budget allocation for campaign ${campaignId}`}
            actions={(
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleSyncMeta}
                  disabled={syncLoading}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  {syncLoading ? 'Syncing Meta…' : 'Sync Meta Spend'}
                </button>
                <button
                  onClick={loadAISuggestions}
                  disabled={aiLoading}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Brain className="h-4 w-4 mr-2" />
                  {aiLoading ? 'Analyzing...' : 'Get AI Suggestions'}
                </button>
                <button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Budget Item
                </button>
              </div>
            )}
          />
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="text-red-800">{error}</div>
          </div>
        )}

        {loading ? (
          <div className="space-y-6">
            <div className="animate-pulse">
              <div className="h-32 bg-gray-100 rounded-lg mb-6"></div>
              <div className="h-64 bg-gray-100 rounded-lg"></div>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Budget Summary */}
            {summary && (
              <BudgetSummaryCard summary={summary} />
            )}

            {/* AI Suggestions */}
            {showSuggestions && (
              <BudgetSuggestionPanel
                suggestions={suggestions}
                reallocations={reallocations}
                loading={aiLoading}
                onAcceptSuggestion={handleAcceptSuggestion}
                onRejectSuggestion={handleRejectSuggestion}
                onAcceptReallocation={handleAcceptReallocation}
                onRejectReallocation={handleRejectReallocation}
              />
            )}

            {/* Budget Categories */}
            {summary && (
              <BudgetCategoryList
                categories={summary.categories}
                onEditCategory={(id) => console.log('Edit category:', id)}
                onDeleteCategory={(id) => console.log('Delete category:', id)}
                onAddBudgetItem={(id) => console.log('Add budget item:', id)}
              />
            )}

            {/* Mapping UI per budget */}
            {budgets.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                  <Link2 className="h-4 w-4 mr-2 text-blue-500" />
                  Link budgets to Meta Campaigns to enable spend sync
                </div>
                {budgets.map(b => (
                  <div key={b.id} className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                    <div className="mb-2 text-sm font-medium text-gray-900 dark:text-white">{b.name} • Budget {b.id}</div>
                    <BudgetMetaMappingPanel
                      budgetId={b.id}
                      existing={mappings[b.id] ? { id: mappings[b.id]!.id, external_campaign_id: mappings[b.id]!.external_campaign_id, budget_id: b.id } : null}
                      onSaved={(m) => setMappings(prev => ({ ...prev, [b.id]: m ? { id: m.id, external_campaign_id: m.external_campaign_id } : null }))}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
